import { Col, Row, Tiles, ExpressiveTile, Typo, Link } from '@geberit/gdds';
import { useRouter } from 'next/navigation';

// styles
import styles from './press-tiles.module.scss';

// types
import type { Material } from '../types/mynewsdesk.types';
import type { PressTilesProps } from '../types/press-portal.types';

// components
import { InnerHtml } from 'components/InnerHtml/inner-html';
import { ExpressiveTile as HeroTile } from 'components/ContentElementsGdds/CustomGdds/Tiles/src/tile/expressive/expressive-tile';

// utils
import { formatDate, getMaterialUniqueIdentifier } from '../utils';
import { useIsMobile, useIsTablet } from 'components/App/SizeProvider';
import { useLocale } from 'utils/use-locale';
import { useTranslationByKey } from 'utils/hooks/use-translations';
import { classNameBuilder } from 'utils/classNameBuilder';
import { decodingContent } from 'utils/decodingContent';

export default function PressTiles({
  language,
  materials,
  withHeroTile = true,
  fallbackImg,
  fallbackBackgroundColor,
}: Readonly<PressTilesProps>) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const locale = useLocale(language);
  const router = useRouter();
  const learnmoreTranslation = useTranslationByKey('press_learn_more');

  const getImageUrl = (material: Material, originSize?: boolean) => {
    if (!material) {
      return undefined;
    }

    if (originSize) return material.image;
    if (isMobile) return material.image_small;
    if (isTablet) return material.image_medium;

    return material.image_medium;
  };

  return (
    <div className={styles.materialsWrapper}>
      <Row>
        <Col>
          <Tiles
            fallbackImg={fallbackImg}
            backgroundColor={fallbackBackgroundColor}
            type="expressive"
          >
            {materials.map((material, index) => {
              const pathname = `?page=detailpage&title=${getMaterialUniqueIdentifier(material)}`;
              const target =
                typeof window !== 'undefined'
                  ? new URL(pathname, window.location.href).toString()
                  : '';
              const link =
                typeof window !== 'undefined'
                  ? {
                      text: learnmoreTranslation,
                      target,
                      hasArrow: false,
                      onClick: (e: Event) => {
                        e.preventDefault();
                        router.push(target);
                      },
                    }
                  : undefined;
              const imageStyles = {
                size: 'cover' as const,
                repeat: false,
              };
              const datetimeText = formatDate(material, locale);

              const commonProps = { link, imageStyles };

              if (withHeroTile && index === 0)
                return (
                  <HeroTile
                    key={material.id}
                    type={'tlea3'}
                    backgroundType="dark"
                    imageSize="m"
                    image={getImageUrl(material, true)}
                  >
                    <>
                      <Typo as="p" className={styles.publishedAt} variant="p2">
                        {datetimeText}
                      </Typo>
                      <InnerHtml
                        as="h3"
                        className={styles.copyText}
                        content={material?.header}
                        isGdds
                      />
                      {link && (
                        <Link
                          stylingType="inverted"
                          href={link.target}
                          text={link.text ? decodingContent(link.text) : link.text}
                          alignByContent="left"
                          windowTarget="_self"
                        />
                      )}
                    </>
                  </HeroTile>
                );

              return (
                <ExpressiveTile
                  key={material.id}
                  type={'tleb1'}
                  noBorder
                  renderHeadline={() => (
                    <Typo
                      as="p"
                      className={classNameBuilder(styles.publishedAt, styles.tleb1)}
                      variant="p2"
                    >
                      {datetimeText}
                    </Typo>
                  )}
                  customParagraph={
                    <InnerHtml
                      className={classNameBuilder(styles.copyText, styles.tleb1)}
                      as="h3"
                      content={material.header}
                      isGdds
                    />
                  }
                  image={getImageUrl(material)}
                  {...commonProps}
                />
              );
            })}
          </Tiles>
        </Col>
      </Row>
    </div>
  );
}
